import HomeIcon from "@mui/icons-material/Home";
import { Button, Typography } from "@mui/material";
import { AuthPageLayout } from "Components/Auth/Shared/AuthPageLayout";
import { AuthRedirectProgress } from "Components/Auth/Shared/AuthRedirectProgress";
import { StyledContainer } from "Components/Auth/Styles";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { useNavigate } from "Navigation/useNavigate";
import { setUnauthenticatedUrl } from "State/Auth/AuthReducer";
import { getUserInfoAsync } from "State/Auth/UserInfo/GetUserInfoState";
import type { RootStateType } from "State/Store";
import { Resources, useResource } from "Translations/Resources";
import { setToken } from "Utils/Storage/LocalStorage/LocalStorageUtils";
import { getAppUrl, resolveStartPage } from "Utils/UrlUtils";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { prefetchUserInfo } from "Auth/Api/Queries/useUserInfoQuery";

export const GoogleCallbackPage = () => {
  const user = useSelector((e: RootStateType) => e.auth.user);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useResource();
  const queryClient = useQueryClient();

  const unauthenticatedUrl = useSelector(
    (e: RootStateType) => e.auth.unauthenticatedUrl,
  );
  // Capture the redirect target at mount. We clear unauthenticatedUrl below to keep
  // it a one-shot redirect; snapshotting it here means the clear can't change where
  // we navigate, and keeps the live value out of the effect deps (no re-run).
  const redirectTargetRef = useRef(unauthenticatedUrl);

  const token = new URLSearchParams(location.search).get("token");
  const error = new URLSearchParams(location.search).get("error");

  // biome-ignore lint/correctness/useExhaustiveDependencies: queryClient is referentially stable, no need to include
  useEffect(() => {
    if (!token) {
      return;
    }
    setToken({ token });

    // React Query prefetch (for new consumers using useUserInfoQuery)
    prefetchUserInfo(queryClient);

    // Redux dispatch (keep for backwards compatibility during migration)
    dispatch(getUserInfoAsync.request(undefined));
  }, [token, dispatch]);

  useEffect(() => {
    if (user) {
      const startUrl = resolveStartPage(user, redirectTargetRef.current);

      if (redirectTargetRef.current !== null) {
        dispatch(setUnauthenticatedUrl(null));
      }

      navigate(startUrl || getAppUrl()("dashboard"));
    }
  }, [user, navigate, dispatch]);

  return (
    <AuthPageLayout title={t(Resources.Auth.Redirecting)} pageType="other">
      <StyledContainer>
        {!error && <AuthRedirectProgress />}
        {!!error && (
          <div>
            <br />
            <Typography>{t(Resources.Error.GoogleLoginFailed)}</Typography>
            <br />
            <UnstyledLink to="/">
              <Button
                startIcon={<HomeIcon />}
                size="large"
                variant="contained"
                color="primary"
              >
                {t(Resources.Errors.Error404.Home)}
              </Button>
            </UnstyledLink>
          </div>
        )}
      </StyledContainer>
    </AuthPageLayout>
  );
};

export default GoogleCallbackPage;
